import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { C } from '../theme'

const STEP_LABELS = {
  1: 'Übernahme', 2: 'Ersatzteile', 3: 'Demontage', 4: 'Lackvorbereitung',
  5: 'Lackierung', 6: 'Lackkontrolle', 7: 'Montage', 8: 'Politur',
  9: 'Aufbereitung', 10: 'Übergabe'
}

export default function Dashboard({ isMobile, onNavigate }) {
  const [stats, setStats]           = useState(null)
  const [monthlyData, setMonthlyData] = useState([])
  const [schrittData, setSchrittData] = useState([])

  useEffect(() => {
    ladeStats()
    // TV-Modus (Display) ile çift yönlü canlı senkron: her değişiklikte yeniden yükle
    const channel = supabase
      .channel('dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'fahrzeuge' }, () => ladeStats())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leihwagen' }, () => ladeStats())
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [])

  const ladeStats = async () => {
    const today        = new Date().toISOString().split('T')[0]
    const weekAgo      = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
    const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0]

    const [
      { data: aktive },
      { data: heuteData },
      { data: wocheData },
      { data: completedData },
      { data: lwData },
    ] = await Promise.all([
      supabase.from('fahrzeuge').select('schritt, problem, fastcars, wunschdatum, eingang').is('übergabe_ok', null),
      supabase.from('fahrzeuge').select('id').eq('übergabe_ok', true).eq('übergabe_datum', today),
      supabase.from('fahrzeuge').select('id').eq('übergabe_ok', true).gte('übergabe_datum', weekAgo),
      supabase.from('fahrzeuge').select('übergabe_datum').eq('übergabe_ok', true).gte('übergabe_datum', sixMonthsAgoStr),
      supabase.from('leihwagen').select('verfügbar'),
    ])

    const now    = new Date()
    const active = aktive || []
    const problemCount  = active.filter(f => f.problem).length
    const fastcarsCount = active.filter(f => f.fastcars).length
    const overdueCount  = active.filter(f => f.wunschdatum && f.wunschdatum < today).length

    const withEingang = active.filter(f => f.eingang)
    const avgDays = withEingang.length > 0
      ? Math.round(withEingang.reduce((sum, f) => sum + (now - new Date(f.eingang)) / 86400000, 0) / withEingang.length)
      : 0

    const lw          = lwData || []
    const lwVerfügbar = lw.filter(l => l.verfügbar).length

    setStats({
      gesamt:    active.length + (completedData || []).length,
      aktiv:     active.length,
      heute:     (heuteData || []).length,
      woche:     (wocheData || []).length,
      problem:   problemCount,
      fastcars:  fastcarsCount,
      overdue:   overdueCount,
      avgDays,
      lwGesamt:  lw.length,
      lwFrei:    lwVerfügbar,
      lwVergeben: lw.length - lwVerfügbar,
    })

    const months = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      months.push({ label: d.toLocaleDateString('de-DE', { month: 'short' }), year: d.getFullYear(), month: d.getMonth(), count: 0 })
    }
    ;(completedData || []).forEach(f => {
      if (!f.übergabe_datum) return
      const d   = new Date(f.übergabe_datum)
      const idx = months.findIndex(m => m.year === d.getFullYear() && m.month === d.getMonth())
      if (idx >= 0) months[idx].count++
    })
    setMonthlyData(months)

    const bySchritt = Array.from({ length: 10 }, (_, i) => ({
      schritt: i + 1,
      label:   STEP_LABELS[i + 1],
      count:   active.filter(f => f.schritt === i + 1).length,
    })).filter(s => s.count > 0)
    setSchrittData(bySchritt)
  }

  if (!stats) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Lädt...</div>
  )

  const MAIN_CARDS = [
    { label: 'Fahrzeuge gesamt',    val: stats.gesamt,   icon: '🚗', color: C.primary, bg: C.primarySoft, ziel: 'fahrzeuge' },
    { label: 'In Bearbeitung',      val: stats.aktiv,    icon: '⚙️', color: C.primary, bg: C.primarySoft, ziel: 'board' },
    { label: 'Heute fertig',        val: stats.heute,    icon: '✅', color: '#22c55e', bg: '#f0fdf4', ziel: 'statistik' },
    { label: 'Diese Woche fertig',  val: stats.woche,    icon: '📅', color: '#16a34a', bg: '#f0fdf4', ziel: 'statistik' },
    { label: 'Problemfahrzeuge',    val: stats.problem,  icon: '⚠️', color: '#ef4444', bg: '#fef2f2', ziel: 'board' },
    { label: 'FastCars-Aufträge',   val: stats.fastcars, icon: '🔧', color: '#8b5cf6', bg: '#faf5ff', ziel: 'board' },
    { label: 'Überfällig',          val: stats.overdue,  icon: '⏰', color: '#dc2626', bg: '#fef2f2', ziel: 'board' },
    { label: 'Ø Bearbeitungsdauer', val: stats.avgDays,  icon: '⏱️', color: '#64748b', bg: '#f8fafc', suffix: 'Tage', ziel: 'statistik' },
  ]

  const LW_CARDS = [
    { label: 'Flotte gesamt',       val: stats.lwGesamt,   icon: '🔑', color: '#3b82f6', bg: '#eff6ff', ziel: 'leihwagen' },
    { label: 'Verfügbar',           val: stats.lwFrei,     icon: '✔️', color: '#22c55e', bg: '#f0fdf4', ziel: 'leihwagen' },
    { label: 'An Kunden vergeben',  val: stats.lwVergeben, icon: '🚗', color: C.primary, bg: C.primarySoft, ziel: 'leihwagen' },
  ]

  const maxMonthly = Math.max(...monthlyData.map(m => m.count), 1)
  const maxSchritt  = Math.max(...schrittData.map(s => s.count), 1)

  const cardCols  = isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'
  const chartCols = isMobile ? '1fr' : '1fr 1fr'

  return (
    <div style={{ padding: isMobile ? '18px 14px' : '32px 36px', maxWidth: 1280, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 2px', fontSize: 26, fontWeight: 800, color: '#111827' }}>Dashboard</h2>
      <p style={{ margin: '0 0 26px', fontSize: 14, color: '#9ca3af' }}>Aktuelle Werkstatt-Übersicht</p>

      <SectionTitle icon="🚗" text="Fahrzeuge in der Werkstatt" />
      <div style={{ display: 'grid', gridTemplateColumns: cardCols, gap: 16, marginBottom: 30 }}>
        {MAIN_CARDS.map(c => <StatCard key={c.label} {...c} onNavigate={onNavigate} />)}
      </div>

      <SectionTitle icon="🔑" text="Leihwagen-Flotte" />
      <div style={{ display: 'grid', gridTemplateColumns: cardCols, gap: 16, marginBottom: 30 }}>
        {LW_CARDS.map(c => <StatCard key={c.label} {...c} onNavigate={onNavigate} />)}
      </div>

      <SectionTitle icon="📊" text="Auswertung" />
      <div style={{ display: 'grid', gridTemplateColumns: chartCols, gap: 18 }}>
        <div style={chartCard}>
          <div style={chartTitle}>Fahrzeuge pro Monat</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110, paddingTop: 20 }}>
            {monthlyData.map(m => (
              <div key={`${m.year}-${m.month}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#374151' }}>{m.count || ''}</div>
                <div style={{
                  width: '100%', background: C.primary, borderRadius: '4px 4px 0 0',
                  height: `${Math.max(Math.round((m.count / maxMonthly) * 74), m.count > 0 ? 3 : 0)}px`,
                }} />
                <div style={{ fontSize: 10, color: '#9ca3af' }}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={chartCard}>
          <div style={chartTitle}>Fahrzeuge pro Schritt (aktiv)</div>
          {schrittData.length === 0
            ? <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 12 }}>Keine aktiven Fahrzeuge</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 12 }}>
                {schrittData.map(s => (
                  <div key={s.schritt} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', width: 88, flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
                    <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 4, height: 16 }}>
                      <div style={{ background: C.primary, height: '100%', width: `${Math.round((s.count / maxSchritt) * 100)}%`, borderRadius: 4 }} />
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', width: 14, textAlign: 'right' }}>{s.count}</div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
      <div style={{ width: 4, height: 18, background: C.primary, borderRadius: 2 }} />
      <span style={{ fontSize: 13, fontWeight: 800, color: '#334155', letterSpacing: 1.2, textTransform: 'uppercase' }}>
        {icon} {text}
      </span>
    </div>
  )
}

function StatCard({ label, val, icon, bg, suffix, ziel, onNavigate }) {
  return (
    <div
      onClick={() => ziel && onNavigate && onNavigate(ziel)}
      style={{
        background: '#fff', borderRadius: 14, padding: '18px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0',
        cursor: ziel && onNavigate ? 'pointer' : 'default', transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 50, height: 50, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 23, flexShrink: 0 }}>
          {icon}
        </div>
        <div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#111827', lineHeight: 1 }}>
            {val}
            {suffix && <span style={{ fontSize: 13, fontWeight: 500, color: '#9ca3af', marginLeft: 4 }}>{suffix}</span>}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>{label}</div>
        </div>
      </div>
    </div>
  )
}

const chartCard  = { background: '#fff', borderRadius: 12, padding: '18px 20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0' }
const chartTitle = { fontSize: 13, fontWeight: 700, color: '#111827' }
